import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { adapter } from "@seek/tealium-adapter";
import { useTranslations } from "@vocab/react";
import { Box, Stack, Text, Inline, Rating, Strong, Badge, Link } from "braid-design-system";
import { translations } from "../../../../../components/.vocab/index.mjs";
import { getMelwaysZone, getLanguage, buildUrlWithQueryParams } from "../../../BrandCard/BrandCardWithAssetLibrary.mjs";
import { companyLogoDimensions, companyLogoBackground, companyLogo, companyLogoDynamicSize, companyTitle, link, companyProfileCard } from "./CompanyProfileCards.css.mjs";
const tealium = adapter();
const CompanyProfileCard = ({
  company,
  showReviews,
  showJobs,
  analyticsQueryParams,
  carouselCardPosition,
  onReviewsLandingPage,
  tabIndex,
  payload,
  featureFlags = {}
}) => {
  const { t } = useTranslations(translations);
  const jobCountNumber = Number(company.jobCount);
  const isSingleJob = jobCountNumber === 1;
  const showJobsBadge = showJobs && jobCountNumber >= 1;
  const cardContentsId = `company_title_${carouselCardPosition}`;
  const melwaysZone = getMelwaysZone(payload == null ? void 0 : payload.zone);
  const language = getLanguage({
    language: payload == null ? void 0 : payload.siteLanguage,
    melwaysZone
  });
  const content = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(
      Box,
      {
        display: "flex",
        flexDirection: "column",
        justifyContent: "spaceBetween",
        height: "full",
        id: cardContentsId,
        children: [
          /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
            /* @__PURE__ */ jsx(
              Box,
              {
                display: "flex",
                alignItems: "center",
                borderRadius: "standard",
                overflow: "hidden",
                className: [
                  companyLogoDimensions,
                  companyLogoBackground,
                  {
                    [companyLogoDynamicSize]: featureFlags.homepageLayoutRefresh
                  }
                ],
                children: /* @__PURE__ */ jsx(
                  Box,
                  {
                    component: "img",
                    src: company.logo,
                    className: companyLogo,
                    alt: "",
                    loading: "lazy"
                  }
                )
              }
            ),
            /* @__PURE__ */ jsx(Box, { className: companyTitle, children: /* @__PURE__ */ jsx(Text, { weight: "medium", maxLines: 2, children: company.title }) })
          ] }),
          showReviews || showJobsBadge ? /* @__PURE__ */ jsx(Box, { paddingTop: "medium", children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
            showReviews ? /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", children: [
              /* @__PURE__ */ jsx(
                Rating,
                {
                  size: "small",
                  rating: company.reviews.rating,
                  variant: "minimal"
                }
              ),
              /* @__PURE__ */ jsx(Text, { size: "small", children: /* @__PURE__ */ jsx(Strong, { children: "·" }) }),
              /* @__PURE__ */ jsx(Text, { size: "small", children: `${company.reviews.count} ${t(
                "Reviews"
              )}` })
            ] }) : null,
            showJobsBadge ? /* @__PURE__ */ jsx(Badge, { tone: onReviewsLandingPage ? "info" : "neutral", children: `${company.jobCount} ${isSingleJob ? t("Job") : t("Jobs")}` }) : null
          ] }) }) : null
        ]
      }
    ),
    /* @__PURE__ */ jsx(
      Link,
      {
        className: link,
        tabIndex,
        href: buildUrlWithQueryParams({
          relativeUrl: `/companies/${company.companyNameSlug}`,
          analyticsQueryParams,
          language,
          zone: melwaysZone
        }),
        data: { testid: "company-profile-card-link" },
        "aria-labelledby": cardContentsId,
        onClick: async () => {
          try {
            tealium.link("company_profile_carousel_card_pressed", {
              companyName: company.title.toLowerCase(),
              companyId: company.id,
              brand: payload == null ? void 0 : payload.brand,
              currentPage: payload == null ? void 0 : payload.currentPage,
              isLoggedIn: payload == null ? void 0 : payload.isLoggedIn,
              siteCountry: payload == null ? void 0 : payload.siteCountry.toLowerCase(),
              siteLanguage: payload == null ? void 0 : payload.siteLanguage,
              loginId: payload == null ? void 0 : payload.loginId,
              zone: payload == null ? void 0 : payload.zone,
              experiments: payload == null ? void 0 : payload.experiments,
              siteSection: payload == null ? void 0 : payload.siteSection,
              cardPosition: carouselCardPosition,
              cardType: payload == null ? void 0 : payload.cardType,
              companyViewOrigin: payload == null ? void 0 : payload.companyViewOrigin
            });
            await new Promise((resolve) => setTimeout(resolve, 500));
          } catch (e) {
            console.error({
              message: "Company profile carousel tealium.link() error",
              error: e
            });
          }
        }
      }
    )
  ] });
  return /* @__PURE__ */ jsx(
    Box,
    {
      component: "article",
      borderRadius: "large",
      height: "full",
      padding: "medium",
      boxShadow: "borderNeutralLight",
      background: "surface",
      className: companyProfileCard,
      children: content
    }
  );
};
export {
  CompanyProfileCard
};
